import { Moment } from '@chipinside/frontend'
import { Translate } from '@chipinside/translate'

import { WithFarm } from '#/store/hooks/useForms/Farm'
import { FormProps, UseFormsProps } from '#/store/hooks/useForms/types'
import { endpoint } from '#/store/services/endpoints'

const systemForms = {
  ccom: {
    attach: ({
      onSuccessAction,
      params,
    }: FormProps<{ serial: string }>): UseFormsProps => ({
      controller: endpoint.ccom.attach_form({ ccom: params?.serial }),
      save: {
        controller: values =>
          endpoint.ccom.farm.attach({
            ccom: params?.serial,
            farm: values?.farm,
          }),
        method: 'put',
        onSuccessActions: [
          {
            enabled: () => true,
            onClick: () => onSuccessAction?.({ serial: params?.serial }),
            title: Translate({
              messageKey: 'config_item',
              params: { item: Translate({ messageKey: 'c_com' }) },
            }),
          },
        ],
      },
      subtitle: Translate({
        messageKey: 'c_com',
        params: { item: params?.serial as string },
      }),
      title: Translate({
        messageKey: 'add_item',
        params: {
          item: Translate({ lower: true, messageKey: 'attachment' }),
        },
      }),
      width: 'xs',
    }),

    config: ({ params }: FormProps<{ serial: string }>): UseFormsProps => ({
      controller: endpoint.ccom.config.form({ ccom: params?.serial }),
      save: {
        controller: endpoint.ccom.config.register({
          ccom: params?.serial,
        }),
        method: 'put',
      },
      subtitle: Translate({
        messageKey: 'c_com',
        params: { item: params?.serial as string },
      }),
      title: Translate({
        messageKey: 'config_item',
        params: { item: Translate({ messageKey: 'c_com' }) },
      }),
      width: 'xl',
    }),

    detach: ({
      params,
    }: FormProps<WithFarm & { serial: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.serial as string,
          item: Translate({
            lower: true,
            messageKey: 'detach_item',
            params: { item: Translate({ messageKey: 'c_com' }) },
          }),
        },
      }),
      save: {
        controller: endpoint.ccom.farm.detach({
          ccom: params?.serial,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: Translate({
        messageKey: 'c_com',
        params: { item: params?.serial as string },
      }),
      title: Translate({
        messageKey: 'detach_item',
        params: { item: Translate({ messageKey: 'c_com' }) },
      }),
    }),

    hexadecimal: ({ params }: FormProps<{ serial: string }>): UseFormsProps => ({
      controller: endpoint.ccom.config.hexkey_form({ ccom: params?.serial }),
      save: {
        controller: endpoint.ccom.config.hexkey_register({
          ccom: params?.serial,
        }),
        method: 'post',
      },
      subtitle: Translate({
        messageKey: 'c_com',
        params: { item: params?.serial as string },
      }),
      title: Translate({
        messageKey: 'config_item',
        params: {
          item: Translate({ lower: true, messageKey: 'hexadecimal_key' }),
        },
      }),
    }),

    replace: ({
      onSuccessAction,
      params,
    }: FormProps<{ serial: string }>): UseFormsProps => ({
      controller: endpoint.ccom.replace_form({ ccom: params?.serial }),
      save: {
        controller: endpoint.ccom.replace({ ccom: params?.serial }),
        method: 'put',
        onSuccessActions: [
          {
            enabled: () => true,
            onClick: ({ values }) => onSuccessAction?.({ serial: values?.serial }),
            title: Translate({
              messageKey: 'config_item',
              params: { item: Translate({ messageKey: 'c_com' }) },
            }),
          },
        ],
      },
      subtitle: Translate({
        messageKey: 'c_com',
        params: { item: params?.serial as string },
      }),
      title: Translate({
        messageKey: 'replace_item',
        params: {
          item: Translate({ messageKey: 'c_com' }),
        },
      }),
      width: 'xs',
    }),
  },

  ccom_maintenance: {
    delete: ({
      params,
    }: FormProps<{
      serial: string | number
      maintenance: string
    }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'error_maintenance' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.ccom.maintenance.self({
          ccom: params?.serial,
          maintenance: params?.maintenance,
        }),
        method: 'delete',
      },
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'error_maintenance' }),
        },
      }),
    }),

    register: ({ params }: FormProps<{ serial: string | number }>): UseFormsProps => ({
      controller: endpoint.ccom.maintenance.form({ ccom: params?.serial }),
      save: {
        controller: endpoint.ccom.maintenance.register({
          ccom: params?.serial,
        }),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'error_maintenance' }),
        },
      }),
      width: 'xs',
    }),

    update: ({
      params,
    }: FormProps<{
      serial: string | number
      maintenance: string
    }>): UseFormsProps => ({
      controller: endpoint.ccom.maintenance.update_form({
        ccom: params?.serial,
        maintenance: params?.maintenance,
      }),
      save: {
        controller: endpoint.ccom.maintenance.self({
          ccom: params?.serial,
          maintenance: params?.maintenance,
        }),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'error_maintenance' }),
        },
      }),
      width: 'xs',
    }),
  },

  ccom_notes: {
    delete: ({ params }: FormProps<WithFarm & { note: string; date: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'note' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.ccom.support_notes.self(params),
        method: 'delete',
      },
      subtitle: params?.date ? Moment.view(params?.date) : undefined,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'note' }),
        },
      }),
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.ccom.support_notes.form(params),
      save: {
        controller: endpoint.ccom.support_notes.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'registration_of',
        params: {
          item: Translate({ lower: true, messageKey: 'note' }),
        },
      }),
      width: 'md',
    }),

    update: ({ params }: FormProps<WithFarm & { note: string }>): UseFormsProps => ({
      controller: endpoint.ccom.support_notes.form_update(params),
      save: {
        controller: endpoint.ccom.support_notes.self(params),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'note' }),
        },
      }),
      width: 'md',
    }),
  },

  cowcomfort: {
    discard: ({ params }: FormProps<{ code: string }>): UseFormsProps => ({
      level: 'error',
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.code as string,
          item: Translate({ lower: true, messageKey: 'discard_cow_comfort' }),
        },
      }),
      save: {
        controller: endpoint.cow_comfort.discard({ c_env: params?.code }),
        method: 'put',
      },
      subtitle: params?.code,
      title: Translate({ messageKey: 'discard' }),
    }),

    revision: ({ params }: FormProps<{ code: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'move_cow_comfort_to',
            params: {
              item: params?.code as string,
              location: Translate({ messageKey: 'revision' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.cow_comfort.revision({ c_env: params?.code }),
        method: 'put',
      },
      subtitle: params?.code,
      title: Translate({
        messageKey: 'move_to',
        params: {
          location: Translate({ lower: true, messageKey: 'revision' }),
        },
      }),
    }),
  },

  ctech: {
    discard: ({ params }: FormProps<{ code: string | number }>): UseFormsProps => ({
      level: 'error',
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.code as string,
          item: Translate({ lower: true, messageKey: 'discard_collar' }),
        },
      }),
      save: {
        controller: endpoint.collar.discard({ collar: params?.code }),
        method: 'put',
      },
      subtitle: params?.code,
      title: Translate({
        messageKey: 'discard_collar',
      }),
    }),

    normalization: ({
      params,
    }: FormProps<{ code: string | number }>): UseFormsProps => ({
      controller: endpoint.collar.event.normalization_form({
        collar: params?.code,
      }),
      save: {
        controller: endpoint.collar.event.normalization({
          collar: params?.code,
        }),
        method: 'post',
      },
      subtitle: params?.code,
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'status_normalization' }),
        },
      }),
    }),

    remove_tag: ({ params }: FormProps<{ code: string | number }>): UseFormsProps => ({
      message: Translate({ messageKey: 'untag_collar_message' }),
      save: {
        controller: endpoint.collar.tags.single.remove({ collar: params?.code }),
        method: 'delete',
      },
      subtitle: params?.code,
      title: Translate({ messageKey: 'untag_collar' }),
    }),

    remove_tag_bulk: ({ params }: FormProps<{ collars: any[] }>): UseFormsProps => {
      const collars = params?.collars
      return {
        message: Translate({ messageKey: 'untag_collar_message_plural' }),
        save: {
          controller: endpoint.collar.tags.bulk.remove(),
          manipulateParams: () => ({ collars }),
          method: 'put',
        },
        subtitle: collars?.join(', '),
        title: Translate({ messageKey: 'untag_collar_plural' }),
      }
    },

    replacement: ({
      params,
    }: FormProps<{ code: string | number }>): UseFormsProps => ({
      controller: endpoint.collar.event.replacement_request_form({
        collar: params?.code,
      }),
      save: {
        controller: endpoint.collar.event.replacement_request({
          collar: params?.code,
        }),
        method: 'post',
      },
      subtitle: params?.code,
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'replacement_request' }),
        },
      }),
    }),

    revision: ({ params }: FormProps<{ code: string | number }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'move_collar_to',
            params: {
              item: params?.code as string,
              location: Translate({ messageKey: 'revision' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.collar.revision({ collar: params?.code }),
        method: 'put',
      },
      subtitle: params?.code,
      title: Translate({
        messageKey: 'move_to',
        params: {
          location: Translate({ lower: true, messageKey: 'revision' }),
        },
      }),
    }),
    tag: ({ params }: FormProps<{ code: string | number }>): UseFormsProps => ({
      controller: endpoint.collar.tags.single.register_form({ collar: params?.code }),
      save: {
        controller: endpoint.collar.tags.single.register({ collar: params?.code }),
        method: 'put',
      },
      subtitle: params?.code,
      title: Translate({ messageKey: 'tag_collar_as' }),
    }),
    tag_bulk: ({ params }: FormProps<{ collars: any[] }>): UseFormsProps => {
      const collars = params?.collars
      return {
        controller: endpoint.collar.tags.bulk.register_form(),
        params,
        save: {
          controller: endpoint.collar.tags.bulk.register(),
          manipulateParams: values => ({ ...values, collars }),
          method: 'put',
        },
        subtitle: collars?.join(', '),
        title: Translate({ messageKey: 'tag_collar_as_plural' }),
      }
    },
  },

  help_pages: {
    delete: ({
      params,
    }: FormProps<{ helpId: string; helpName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.helpName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'help' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.database.help_pages.self({
          help: params?.helpId,
        }),
        method: 'delete',
      },
      subtitle: params?.helpName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'help' }),
        },
      }),
    }),
    register: (): UseFormsProps => ({
      controller: endpoint.database.help_pages.register_form(),
      save: {
        controller: endpoint.database.help_pages.register(),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'help' }),
        },
      }),
      width: 'lg',
    }),
    update: ({
      params,
    }: FormProps<{ helpId: string; helpName: string }>): UseFormsProps => ({
      controller: endpoint.database.help_pages.update_form({
        help: params?.helpId,
      }),
      save: {
        controller: endpoint.database.help_pages.self({
          help: params?.helpId,
        }),
        method: 'put',
      },
      subtitle: params?.helpName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'help' }),
        },
      }),
      width: 'lg',
    }),
  },

  mail: {
    update: ({ params }: FormProps<{ type: string; emailName: string }>): UseFormsProps => ({
      controller: endpoint.system.transactional_emails.update_form(params),
      save: {
        controller: endpoint.system.transactional_emails.update(params),
        method: 'put',
      },
      subtitle: params?.emailName,
      title: Translate({ messageKey: 'transactional_emails' }),
      width: 'xs',
    }),
  },

  manufacturer: {
    delete: ({ params }: FormProps<{ id: string; manufacturerName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.manufacturerName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'manufacturer' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.database.manufacturer.self(params),
        method: 'delete',
      },
      subtitle: params?.manufacturerName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'manufacturer' }),
        },
      }),
    }),

    register: (): UseFormsProps => ({
      controller: endpoint.database.manufacturer.register_form(),
      save: {
        controller: endpoint.database.manufacturer.register(),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'manufacturer' }),
        },
      }),
      width: 'xs',
    }),

    update: ({ params }: FormProps<{ id: string }>): UseFormsProps => ({
      controller: endpoint.database.manufacturer.update_form(params),
      save: {
        controller: endpoint.database.manufacturer.self(params),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'manufacturer' }),
        },
      }),
      width: 'xs',
    }),
  },

  new_semen_bull: {
    delete: ({ params }: FormProps<{ semenId: string; semenName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.semenName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'bull_or_semen' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.database.new_semen.remove({ semen: params?.semenId }),
        method: 'delete',
      },
      subtitle: params?.semenName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'bull_or_semen' }),
        },
      }),
    }),

    register: (): UseFormsProps => ({
      controller: endpoint.database.new_semen.register_form(),
      save: {
        controller: endpoint.database.new_semen.register(),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'bull_or_semen' }),
        },
      }),
      width: 'md',
    }),

    restore: ({ params }: FormProps<{ semenId: string; semenName: string }>): UseFormsProps => ({
      level: 'warning',
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: Translate({ lower: true, messageKey: 'bull_or_semen' }),
          item: Translate({ lower: true, messageKey: 'restore_this' }),
        },
      }),
      save: {
        controller: endpoint.database.new_semen.restore({ semen: params?.semenId }),
        method: 'patch',
      },
      submitButton: {
        title: Translate({ messageKey: 'restore' }),
      },
      subtitle: params?.semenName,
      title: Translate({
        messageKey: 'restore_item',
        params: {
          item: Translate({ lower: true, messageKey: 'bull_or_semen' }),
        },
      }),
    }),

    update: ({ params }: FormProps<{ semenId: string; semenName: string }>): UseFormsProps => ({
      controller: endpoint.database.new_semen.update_form({
        semen: params?.semenId,
      }),
      save: {
        controller: endpoint.database.new_semen.update({ semen: params?.semenId }),
        method: 'put',
      },
      subtitle: params?.semenName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'bull_or_semen' }),
        },
      }),
      width: 'md',
    }),
  },

  nps_campaings: {
    delete: ({
      params,
    }: FormProps<{
      campaignId: string
      campaignName: string
    }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.campaignName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'campaign' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.cs.nps.campaigns.delete({
          campaign: params?.campaignId,
        }),
        method: 'delete',
      },
      subtitle: params?.campaignName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'campaign' }),
        },
      }),
    }),

    register: (): UseFormsProps => ({
      controller: endpoint.cs.nps.campaigns.register_form(),
      save: {
        controller: endpoint.cs.nps.campaigns.register(),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'campaign' }),
        },
      }),
      width: 'xs',
    }),

    update: ({
      params,
    }: FormProps<{
      campaignId: string
      campaignName: string
    }>): UseFormsProps => ({
      controller: endpoint.cs.nps.campaigns.update_form({
        campaign: params?.campaignId,
      }),
      save: {
        controller: endpoint.cs.nps.campaigns.update({
          campaign: params?.campaignId,
        }),
        method: 'put',
      },
      subtitle: params?.campaignName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'campaign' }),
        },
      }),
      width: 'xs',
    }),
  },

  rags: {
    fixed_update: ({ params }: FormProps<{ ragId: string; ragName: string }>): UseFormsProps => ({
      controller: endpoint.rags.fixed.update_form({ rag: params?.ragId }),
      save: {
        controller: endpoint.rags.fixed.update({ rag: params?.ragId }),
        method: 'put',
      },
      subtitle: params?.ragName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ messageKey: 'rag', upper: true }),
        },
      }),
      width: 'xl',
    }),
    resources_delete: ({
      params,
    }: FormProps<{ ragId: string; ragName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'remove_rag',
        params: {
          item: params?.ragName as string,
        },
      }),
      save: {
        controller: endpoint.rags.resources.delete({ rag: params?.ragId }),
        method: 'delete',
      },
      subtitle: params?.ragName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: false, messageKey: 'rag' }),
        },
      }),
    }),
    resources_register: (): UseFormsProps => ({
      controller: endpoint.rags.resources.register_form(),
      save: {
        controller: endpoint.rags.resources.register(),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ messageKey: 'rag' }),
        },
      }),
      width: 'xl',
    }),
    resources_update: ({
      params,
    }: FormProps<{ ragId: string; ragName: string }>): UseFormsProps => ({
      controller: endpoint.rags.resources.update_form({ rag: params?.ragId }),
      save: {
        controller: endpoint.rags.resources.update({ rag: params?.ragId }),
        method: 'put',
      },
      subtitle: params?.ragName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ messageKey: 'rag', upper: true }),
        },
      }),
      width: 'xl',
    }),
  },

  release_notes: {
    delete: ({ params }: FormProps<{ id: string; releaseTitle: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.releaseTitle as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'release_notes' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.system.release_notes.remove(params),
        method: 'delete',
      },
      subtitle: params?.releaseTitle,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'release_notes' }),
        },
      }),
    }),

    register: (): UseFormsProps => ({
      controller: endpoint.system.release_notes.register_form(),
      save: {
        controller: endpoint.system.release_notes.register(),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'release_notes' }),
        },
      }),
      width: 'lg',
    }),

    update: ({ params }: FormProps<{ id: string; releaseTitle: string }>): UseFormsProps => ({
      controller: endpoint.system.release_notes.update_form(params),
      save: {
        controller: endpoint.system.release_notes.update(params),
        method: 'put',
      },
      subtitle: params?.releaseTitle,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'release_notes' }),
        },
      }),
      width: 'lg',
    }),
  },
  semen_bull: {
    delete: ({ params }: FormProps<{ semenId: string; semenName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.semenName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'bull_or_semen' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.database.semen.remove({ semen: params?.semenId }),
        method: 'delete',
      },
      subtitle: params?.semenName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'bull_or_semen' }),
        },
      }),
    }),

    register: (): UseFormsProps => ({
      controller: endpoint.database.semen.register_form(),
      save: {
        controller: endpoint.database.semen.register(),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'bull_or_semen' }),
        },
      }),
      width: 'md',
    }),

    replace: (): UseFormsProps => ({
      controller: endpoint.database.semen.replace_form(),
      save: {
        controller: endpoint.database.semen.replace(),
        method: 'put',
      },
      title: Translate({ messageKey: 'replace_records' }),
    }),

    update: ({ params }: FormProps<{ semenId: string; semenName: string }>): UseFormsProps => ({
      controller: endpoint.database.semen.update_form({
        semen: params?.semenId,
      }),
      save: {
        controller: endpoint.database.semen.update({ semen: params?.semenId }),
        method: 'put',
      },
      subtitle: params?.semenName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'bull_or_semen' }),
        },
      }),
      width: 'md',
    }),
  },

  semen_company: {
    delete: ({
      params,
    }: FormProps<{
      semenCompanyId: string
      semenCompanyName: string
    }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.semenCompanyName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'company' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.database.semen_company.remove({
          semen_company: params?.semenCompanyId,
        }),
        method: 'delete',
      },
      subtitle: params?.semenCompanyName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'company' }),
        },
      }),
    }),

    register: (): UseFormsProps => ({
      controller: endpoint.database.semen_company.register_form(),
      save: {
        controller: endpoint.database.semen_company.register(),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'company' }),
        },
      }),
    }),

    update: ({
      params,
    }: FormProps<{
      semenCompanyId: string
      semenCompanyName: string
    }>): UseFormsProps => ({
      controller: endpoint.database.semen_company.update_form({
        semen_company: params?.semenCompanyId,
      }),
      save: {
        controller: endpoint.database.semen_company.update({
          semen_company: params?.semenCompanyId,
        }),
        method: 'put',
      },
      subtitle: params?.semenCompanyName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'company' }),
        },
      }),
    }),
  },

  system_permissions: {
    update: ({ params }: FormProps<{ permission: string }>): UseFormsProps => ({
      controller: endpoint.system.permissions.update_form(params),
      save: {
        controller: endpoint.system.permissions.update(params),
        method: 'put',
      },
      submitButton: {
        title: Translate({ messageKey: 'update_item' }),
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'permission' }),
        },
      }),
      width: 'xs',
    }),
  },

  system_role: {
    delete: ({ params }: FormProps<{ role: string; roleName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.roleName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'role' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.system.role.self(params),
        method: 'delete',
      },
      subtitle: params?.roleName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'role' }),
        },
      }),
    }),

    duplicate: ({ params }: FormProps<{ role: string }>): UseFormsProps => ({
      controller: endpoint.system.role.duplicate_form(params),
      save: {
        controller: endpoint.system.role.duplicate(params),
        manipulateParams(values) {
          return {
            ...values,
            ...(params?.role ? { role_slug: params.role } : {}),
          }
        },
        method: 'post',
      },
      title: Translate({ messageKey: 'duplicate_role' }),
      width: 'xs',
    }),

    register: (): UseFormsProps => ({
      controller: endpoint.system.role.register_form(),
      save: {
        controller: endpoint.system.role.register(),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'role' }),
        },
      }),
      width: 'xs',
    }),

    update: ({ params }: FormProps<{ role: string; roleName: string }>): UseFormsProps => ({
      controller: endpoint.system.role.update_form(params),
      save: {
        controller: endpoint.system.role.self(params),
        method: 'put',
      },
      subtitle: params?.roleName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'role' }),
        },
      }),
      width: 'xs',
    }),
  },

  system_routes: {
    roles: ({ params }: FormProps<{ route: string }>): UseFormsProps => ({
      controller: endpoint.system.routes.roles_form(params),
      save: {
        controller: endpoint.system.routes.roles(params),
        method: 'put',
      },
      title: Translate({ messageKey: 'attach_permissions_to_roles' }),
      width: 'lg',
    }),
  },

  token_usage: {
    farm_limit_update: ({ params }: FormProps<{ farm: string }>): UseFormsProps => ({
      controller: endpoint.token_usage.farm.limit_update_form(params),
      save: {
        controller: endpoint.token_usage.farm.limit_update(params),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'limit' }),
        },
      }),
      width: 'xs',
    }),
  },

  treatments: {
    delete: ({ params }: FormProps<{ id: string; treatmentName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.treatmentName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'treatment' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.database.treatment.self(params),
        method: 'delete',
      },
      subtitle: params?.treatmentName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'treatment' }),
        },
      }),
    }),

    register: (): UseFormsProps => ({
      controller: endpoint.database.treatment.register_form(),
      save: {
        controller: endpoint.database.treatment.register(),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'treatment' }),
        },
      }),
    }),

    update: ({ params }: FormProps<{ id: string; treatmentName: string }>): UseFormsProps => ({
      controller: endpoint.database.treatment.update_form(params),
      save: {
        controller: endpoint.database.treatment.self(params),
        method: 'put',
      },
      subtitle: params?.treatmentName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'treatment' }),
        },
      }),
    }),
  },
}
export { systemForms }
